// GlobalRainModeGesture
// =====================
//
// Wave 14dt: globale Geste zum AKTIVIEREN von Rain-Mode — Single-Finger
// Long-Press (3 s ohne Bewegung), von jeder App-Seite aus. Ergänzt den
// dedizierten 🌧️-Button (der bleibt). User-Wunsch: mit Handschuhen schnell
// erreichbar, ohne erst zur Map zu navigieren.
//
// Abgrenzung:
//   - NUR Aktivierung. Das Verlassen bleibt die bewusste 3-Finger-2s-Geste
//     (RainModeOverlay) — gegen versehentliche Regentropfen-Touches.
//   - Single-Finger (touches.length === 1) → kollidiert NICHT mit der
//     3-Finger-Exit-Geste.
//   - Bewegung > THRESHOLD canceled den Timer → kollidiert nicht mit
//     Map-Pan, Scroll, Swipe.
//   - Text-editierbare Targets (input/textarea/contenteditable) werden
//     übersprungen — dort ist Long-Press die native iOS-Text-Selektion.
//   - Feature-Gate: nur wenn Rain-Mode in den Settings aktiviert ist.
//   - No-op wenn Rain- oder Pocket-Mode schon aktiv ist (deren Overlays
//     fangen Touches eh ab, aber doppelt hält besser).
//
// Rendert nichts (null) — reine Document-Listener via Effect.

import { useEffect } from 'react';
import { rainModeBus } from '../lib/rainModeBus';
import { pocketModeBus } from '../lib/pocketModeBus';
import { useRainModeEnabled } from '../lib/useUiSettings';

const LONG_PRESS_MS = 3000;
const MOVE_CANCEL_PX = 10;

export function GlobalRainModeGesture() {
  const [rainEnabled] = useRainModeEnabled();

  useEffect(() => {
    if (!rainEnabled) return;

    let timer: number | null = null;
    let startX = 0;
    let startY = 0;

    const clear = () => {
      if (timer != null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      // Nur Single-Finger. Multi-Touch (inkl. der 3-Finger-Exit-Geste) ignorieren.
      if (e.touches.length !== 1) {
        clear();
        return;
      }
      // Schon aktiv (Rain ODER Pocket) → keine neue Aktivierung.
      if (rainModeBus.get() || pocketModeBus.get()) return;
      // Text-editierbare Targets überspringen (native Long-Press = Selektion).
      const target = e.target as Element | null;
      if (
        target?.closest?.(
          'input, textarea, select, [contenteditable=""], [contenteditable="true"]',
        )
      ) {
        return;
      }
      const t = e.touches[0]!;
      startX = t.clientX;
      startY = t.clientY;
      clear();
      timer = window.setTimeout(() => {
        timer = null;
        if (!rainModeBus.get() && !pocketModeBus.get()) {
          rainModeBus.activate();
        }
      }, LONG_PRESS_MS);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (timer == null) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.hypot(dx, dy) > MOVE_CANCEL_PX) clear();
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', clear, { passive: true });
    document.addEventListener('touchcancel', clear, { passive: true });
    return () => {
      clear();
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', clear);
      document.removeEventListener('touchcancel', clear);
    };
  }, [rainEnabled]);

  return null;
}
